import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandEmpty,
  CommandExpandContext,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function ScorecardsCommandList({
  placeholder = 'Filter Scorecards...',
  selected = [],
  onSelect,
  multiselect,
  excludeIds,
  contextFilters,
  onCreate,
}: Props) {
  const [condensedList, setCondensedList] = useState(true)
  const [query, setQuery] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const { data, isInitialLoading } = useDatastore(FilterCollection.Scorecards)

  const baseList = useMemo(
    () => data?.filter((scorecard) => !excludeIds?.includes(scorecard.id)),
    [data, excludeIds],
  )

  const filteredList = useMemo(() => {
    if (!condensedList) return baseList

    return baseList?.filter((scorecard) => {
      if (!contextFilters) return true
      const { workspaceId } = contextFilters
      if (workspaceId && '$eq' in workspaceId && workspaceId.$eq.length > 0)
        return workspaceId.$eq.includes(scorecard.meta.workspaceId)
      return true
    })
  }, [baseList, condensedList, contextFilters])

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return filteredList
    const normalizedQuery = query.toLowerCase()
    return filteredList?.filter(
      (scorecard) =>
        scorecard.name.toLowerCase().includes(normalizedQuery) ||
        (scorecard.meta.workspaceName || 'Organization')
          .toLowerCase()
          .includes(normalizedQuery),
    )
  }, [filteredList, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  const expandedResults = (baseList?.length || 0) - (filteredList?.length || 0)
  const trimmedQuery = query.trim()
  const hasExactNameMatch = useMemo(() => {
    if (!trimmedQuery) return true
    const normalizedQuery = trimmedQuery.toLowerCase()
    return (baseList || []).some(
      (scorecard) => scorecard.name.trim().toLowerCase() === normalizedQuery,
    )
  }, [trimmedQuery, baseList])
  const showCreateOption = Boolean(onCreate && trimmedQuery && !hasExactNameMatch)

  const createLabel = `Create "${trimmedQuery}"`

  const handleCreate = useCallback(async () => {
    const name = trimmedQuery
    if (!onCreate || !name || isCreating) return

    setIsCreating(true)
    try {
      await onCreate(name)
      setQuery('')
    } finally {
      setIsCreating(false)
    }
  }, [onCreate, trimmedQuery, isCreating])

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder={placeholder}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {showCreateOption && (
            <CommandItem
              value={createLabel}
              keywords={[trimmedQuery, 'create scorecard']}
              onSelect={() => {
                void handleCreate()
              }}
              disabled={isCreating}
            >
              {createLabel}
            </CommandItem>
          )}
          {visibleItems?.map((scorecard) => (
            <CommandCheckItem
              key={scorecard.id}
              value={scorecard.id}
              keywords={[scorecard.name, scorecard.meta.workspaceName || 'Organization']}
              selected={selected.includes(scorecard.id)}
              onSelect={() => handleToggleItem(scorecard.id)}
              multiselect={multiselect}
            >
              {scorecard.name}{' '}
              <span className="text-accent-foreground text-xs">
                {scorecard.meta.workspaceName || 'Organization'}
              </span>
            </CommandCheckItem>
          ))}
          <CommandExpandContext
            availableResults={expandedResults}
            onSelect={() => setCondensedList(false)}
          />
          <CommandEmpty>
            <p className="text-accent-foreground p-4">
              {onCreate
                ? 'No scorecards to add. Type a name to create one.'
                : 'No scorecards found.'}
            </p>
          </CommandEmpty>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

interface Props extends BaseCommandListProps<false, string> {
  contextFilters?: {
    workspaceId?: {
      $eq: string[]
    }
  }
  onCreate?: (name: string) => void | Promise<void>
}
